import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Ban, Download, MessageSquare } from 'lucide-react';
import { api, apiDownload, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { ComentariosDialog } from '@/components/domain/ComentariosDialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ESTATUS_REPORTE,
  ESTATUS_REPORTE_LABEL,
  type EstatusReporte,
  type Paginated,
  type ReporteBookingRow,
} from '@/lib/types';

const TRANSPORTES = ['SEA', 'ROA', 'AIR'];
const PRODUCTOS = ['FCL', 'FTL', 'LCL', 'LTL', 'LSE'];
const ALL = '__all__';

const ESTATUS_CLASS: Record<EstatusReporte, string> = {
  abierto: 'bg-ink-100 text-ink-700',
  en_transito: 'bg-blue-100 text-blue-700',
  pre_arribo: 'bg-amber-100 text-amber-700',
  arribado: 'bg-green-100 text-green-700',
  vencido: 'bg-red-100 text-red-700',
  cerrado: 'bg-ink-200 text-ink-600',
  sin_fecha: 'bg-ink-100 text-ink-500',
};

function fmtFecha(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

interface Filtros {
  estatus?: EstatusReporte;
  transporte?: string;
  modo?: string;
  agenteId?: number;
  estatusFacturacion?: string;
  estatusPago?: string;
  search: string;
  page: number;
  perPage: number;
  soloDetenidos?: boolean;
}

const DEFAULT: Filtros = { search: '', page: 1, perPage: 100 };
const PER_PAGE_OPCIONES = [100, 250, 500, 1000];

export function ReportesBookingsPage() {
  const { user } = useAuth();
  // ventas es solo lectura + comentar: sin acción "Detener".
  const readOnly = user?.rol === 'ventas';
  const [f, setF] = useState<Filtros>(DEFAULT);
  const [exporting, setExporting] = useState(false);
  const qc = useQueryClient();
  const [comentarioModal, setComentarioModal] = useState<ReporteBookingRow | null>(null);
  const [detenerModal, setDetenerModal] = useState<ReporteBookingRow | null>(null);
  const [reanudarModal, setReanudarModal] = useState<ReporteBookingRow | null>(null);
  const refetchReporte = () => qc.invalidateQueries({ queryKey: ['reporte-bookings'] });

  const queryParams = {
    estatus: f.estatus,
    soloDetenidos: f.soloDetenidos ? '1' : undefined,
    transporte: f.transporte,
    modo: f.modo,
    agenteId: f.agenteId,
    estatusFacturacion: f.estatusFacturacion,
    estatusPago: f.estatusPago,
    search: f.search || undefined,
    page: f.page,
    perPage: f.perPage,
  };

  const reporteQ = useQuery({
    queryKey: ['reporte-bookings', f],
    queryFn: () =>
      api<Paginated<ReporteBookingRow>>('/reports/bookings', { params: queryParams }),
  });

  // Operativos para el filtro (supervisor/admin pueden ver el equipo)
  const agentesQ = useQuery({
    queryKey: ['reporte-agentes'],
    queryFn: () =>
      api<{ items: { id: number; nombre: string }[] }>('/team/agentes'),
  });

  async function onExport() {
    setExporting(true);
    try {
      await apiDownload('/reports/bookings/export', 'Reporte OPS.xlsx', {
        estatus: f.estatus,
        transporte: f.transporte,
        modo: f.modo,
        agenteId: f.agenteId,
        estatusFacturacion: f.estatusFacturacion,
        estatusPago: f.estatusPago,
        search: f.search || undefined,
      });
    } finally {
      setExporting(false);
    }
  }

  const data = reporteQ.data;
  const rows = data?.items ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 w-[95%] max-w-none mx-auto">
      {/* Scrollbars siempre visibles (incl. la horizontal) en la tabla */}
      <style>{`
        .report-scroll { overflow: scroll; max-height: 70vh; }
        .report-scroll::-webkit-scrollbar { width: 14px; height: 14px; }
        .report-scroll::-webkit-scrollbar-track { background: #f1f5f9; }
        .report-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 7px; border: 3px solid #f1f5f9; }
        .report-scroll { scrollbar-width: auto; scrollbar-color: #94a3b8 #f1f5f9; }
      `}</style>
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold">Reportes · Bookings</h1>
          <p className="text-sm text-ink-400">
            {data ? `${data.pagination.total} bookings` : 'Cargando…'}
          </p>
        </div>
        <Button onClick={onExport} disabled={exporting || !rows.length} size="sm">
          <Download className="h-4 w-4 mr-1" />
          {exporting ? 'Generando…' : 'Exportar a Excel'}
        </Button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 p-2 rounded-lg bg-surface-2">
        <Input
          placeholder="Buscar posición o cliente…"
          value={f.search}
          onChange={(e) => setF({ ...f, search: e.target.value, page: 1 })}
          className="w-56"
        />
        <Select
          value={f.estatus ?? ALL}
          onValueChange={(v) =>
            setF({ ...f, estatus: v === ALL ? undefined : (v as EstatusReporte), page: 1 })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Estatus" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todos los envíos</SelectItem>
            {ESTATUS_REPORTE.map((e) => (
              <SelectItem key={e} value={e}>
                {ESTATUS_REPORTE_LABEL[e]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={f.transporte ?? ALL}
          onValueChange={(v) => setF({ ...f, transporte: v === ALL ? undefined : v, page: 1 })}
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todo tipo</SelectItem>
            {TRANSPORTES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={f.modo ?? ALL}
          onValueChange={(v) => setF({ ...f, modo: v === ALL ? undefined : v, page: 1 })}
        >
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Producto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todo producto</SelectItem>
            {PRODUCTOS.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={f.agenteId ? String(f.agenteId) : ALL}
          onValueChange={(v) =>
            setF({ ...f, agenteId: v === ALL ? undefined : Number(v), page: 1 })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Operativo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Todo operativo</SelectItem>
            {(agentesQ.data?.items ?? []).map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={f.estatusFacturacion ?? ALL}
          onValueChange={(v) =>
            setF({ ...f, estatusFacturacion: v === ALL ? undefined : v, page: 1 })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Estatus facturación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Estatus facturación</SelectItem>
            <SelectItem value="Facturado">Facturado</SelectItem>
            <SelectItem value="Pendiente">Pendiente</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={f.estatusPago ?? ALL}
          onValueChange={(v) => setF({ ...f, estatusPago: v === ALL ? undefined : v, page: 1 })}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Estatus pago" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Estatus pago</SelectItem>
            <SelectItem value="Pagado">Pagado</SelectItem>
            <SelectItem value="No pagado">No pagado</SelectItem>
            <SelectItem value="Sin dato">Sin dato</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-1.5 text-sm text-ink-600 cursor-pointer select-none" title="Mostrar solo los embarques con carga detenida">
          <input
            type="checkbox"
            className="h-4 w-4 accent-red-600"
            checked={!!f.soloDetenidos}
            onChange={(e) => setF({ ...f, soloDetenidos: e.target.checked || undefined, page: 1 })}
          />
          Mostrar detenidos
        </label>
        {(f.estatus || f.transporte || f.modo || f.agenteId || f.estatusFacturacion || f.estatusPago || f.soloDetenidos || f.search) && (
          <Button variant="ghost" size="sm" onClick={() => setF(DEFAULT)}>
            Limpiar
          </Button>
        )}
      </div>

      {/* Tabla estilo Excel */}
      <div className="report-scroll rounded-lg border border-ink-200 bg-surface-1">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-surface-2 text-xs uppercase tracking-wider text-ink-400">
              {[
                'Posición (BO)',
                'Tipo',
                'Producto',
                'Origen país',
                'Destino país',
                'Cliente',
                'Comercial',
                'Operativo',
                'Fecha creación BO',
                'ETD',
                'C.ETD',
                'ETA',
                'C.ETA',
                'Estatus Embarque',
                'Estatus Facturación',
                'Estatus Pago',
                'Días crédito',
                'Cartera vencida',
                'Detención',
                'Comentarios',
              ].map((h) => (
                <th
                  key={h}
                  className={cn(
                    'px-3 py-2 text-left font-semibold border border-ink-100 whitespace-nowrap',
                    h === 'Detención' && 'cursor-pointer hover:text-red-600 select-none',
                    h === 'Detención' && f.soloDetenidos && 'text-red-600 underline',
                  )}
                  onClick={
                    h === 'Detención'
                      ? () => setF({ ...f, soloDetenidos: !f.soloDetenidos || undefined, page: 1 })
                      : undefined
                  }
                  title={
                    h === 'Cartera vencida'
                      ? 'El cliente tiene ≥1 factura vencida (en cualquier embarque, incluso anteriores)'
                      : h === 'Detención'
                        ? 'Click para mostrar solo los embarques detenidos'
                        : undefined
                  }
                >
                  {h}
                  {h === 'Detención' && f.soloDetenidos && ' ✓'}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reporteQ.isLoading && (
              <tr>
                <td colSpan={18} className="px-3 py-6 text-center text-ink-400">
                  Cargando…
                </td>
              </tr>
            )}
            {!reporteQ.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={18} className="px-3 py-6 text-center text-ink-400">
                  Sin resultados con estos filtros.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r.posicion}-${i}`} className="hover:bg-surface-1">
                <td className="px-3 py-1.5 border border-ink-100 font-medium whitespace-nowrap">{r.posicion}</td>
                <td className="px-3 py-1.5 border border-ink-100">{r.tipo || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100">{r.producto || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-ink-400">{r.origenPais ?? '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 text-ink-400">{r.destinoPais ?? '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{r.cliente || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{r.comercial ?? '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{r.operativo || '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{fmtFecha(r.fechaCreacionBO)}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{fmtFecha(r.etd)}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap" title="Journey ETD: puerto/aeropuerto origen → destino">{fmtFecha(r.cEtd)}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">{fmtFecha(r.eta)}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap" title="Journey ETA: puerto/aeropuerto origen → destino">{fmtFecha(r.cEta)}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium', ESTATUS_CLASS[r.estatus])}>
                    {ESTATUS_REPORTE_LABEL[r.estatus]}
                  </span>
                </td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  <span
                    className={cn(
                      'inline-block px-2 py-0.5 rounded text-xs font-medium',
                      r.estatusFacturacion === 'Facturado'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700',
                    )}
                  >
                    {r.estatusFacturacion}
                  </span>
                </td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  {r.estatusPago ? (
                    <span
                      className={cn(
                        'inline-block px-2 py-0.5 rounded text-xs font-medium',
                        r.estatusPago === 'Pagado'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700',
                      )}
                    >
                      {r.estatusPago}
                    </span>
                  ) : (
                    <span className="text-ink-300">—</span>
                  )}
                </td>
                <td className="px-3 py-1.5 border border-ink-100 text-ink-400">{r.diasCredito ?? '—'}</td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  {r.facturasVencidas ? (
                    <span className="text-red-700 text-xs font-semibold">Sí</span>
                  ) : (
                    <span className="text-ink-400 text-xs">No</span>
                  )}
                </td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  {r.detenido ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold">
                        <Ban className="h-3.5 w-3.5" /> Detenido
                      </span>
                      {!readOnly && (
                        <button
                          onClick={() => setReanudarModal(r)}
                          className="rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-medium px-2 py-0.5"
                          title="Reanudar la liberación de esta carga"
                        >
                          Reanudar
                        </button>
                      )}
                    </span>
                  ) : readOnly ? (
                    <span className="text-ink-400 text-xs">—</span>
                  ) : (
                    <button
                      onClick={() => setDetenerModal(r)}
                      className="inline-flex items-center gap-1 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-2 py-1"
                    >
                      <Ban className="h-3.5 w-3.5" /> Detener
                    </button>
                  )}
                </td>
                <td className="px-3 py-1.5 border border-ink-100 whitespace-nowrap">
                  <button
                    onClick={() => setComentarioModal(r)}
                    title="Ver / agregar comentarios"
                    className={cn(
                      'inline-flex items-center gap-1 rounded border px-2 py-1 text-xs',
                      r.comentario
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 text-ink-500 hover:bg-surface-2',
                    )}
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Comentarios{r.comentariosCount > 0 ? ` (${r.comentariosCount})` : ''}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      {data && data.pagination.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-ink-400">Mostrar</span>
            <Select
              value={String(f.perPage)}
              onValueChange={(v) => setF({ ...f, perPage: Number(v), page: 1 })}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPCIONES.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-ink-400">de {data.pagination.total} registros</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-ink-400">
              Página {f.page} de {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={f.page <= 1}
              onClick={() => setF({ ...f, page: f.page - 1 })}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={f.page >= totalPages}
              onClick={() => setF({ ...f, page: f.page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {comentarioModal && (
        <ComentariosDialog
          open={!!comentarioModal}
          onOpenChange={(o) => !o && setComentarioModal(null)}
          bookingId={comentarioModal.id}
          bookingIdExterno={comentarioModal.posicion}
          cliente={comentarioModal.cliente}
        />
      )}
      {reanudarModal && (
        <ReanudarModal
          row={reanudarModal}
          onClose={() => setReanudarModal(null)}
          onDone={() => {
            refetchReporte();
            setReanudarModal(null);
          }}
        />
      )}
      {detenerModal && (
        <DetenerModal
          row={detenerModal}
          onClose={() => setDetenerModal(null)}
          onDone={() => {
            refetchReporte();
            setDetenerModal(null);
          }}
        />
      )}
    </div>
  );
}

/** Modal de confirmación para reanudar (liberar) una carga detenida. */
function ReanudarModal({
  row,
  onClose,
  onDone,
}: {
  row: ReporteBookingRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const [motivo, setMotivo] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      api('/reports/bookings/reanudar', {
        method: 'POST',
        body: JSON.stringify({ bookingIdExterno: row.posicion, vendedorEmail: email, motivo }),
      }),
    onSuccess: () => {
      toast.success('Carga liberada: motivo guardado en Comentarios y aviso a los involucrados');
      onDone();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'No se pudo reanudar'),
  });
  const emailOk = /.+@.+\..+/.test(email);
  const motivoOk = motivo.trim().length >= 5;
  return (
    <ModalShell titulo={`Liberar carga · ${row.posicion}`} onClose={onClose}>
      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
        La detención quedará sin efecto. El <b>motivo es obligatorio</b>: se guarda en
        el hilo de Comentarios del embarque (historial de responsabilidad) y se avisa
        por correo y campanita a los involucrados.
      </div>
      <label className="block text-sm mt-3 mb-1 text-ink-600">
        Motivo del porqué se puede liberar el embarque
      </label>
      <textarea
        autoFocus
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        placeholder="Ej. El cliente liquidó el saldo vencido / autorización de gerencia administrativa…"
        className="w-full min-h-[5rem] px-3 py-2 text-sm border border-ink-200 rounded-md bg-surface-1"
      />
      <label className="block text-sm mt-3 mb-1 text-ink-600">
        Correo del vendedor/comercial a notificar
      </label>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="vendedor@strd.com.mx"
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={() => mut.mutate()} disabled={!emailOk || !motivoOk || mut.isPending}>
          {mut.isPending ? 'Liberando…' : 'Liberar carga'}
        </Button>
      </div>
    </ModalShell>
  );
}

/** Modal de confirmación para detener la carga (alerta por correo). */
function DetenerModal({
  row,
  onClose,
  onDone,
}: {
  row: ReporteBookingRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [email, setEmail] = useState('');
  const mut = useMutation({
    mutationFn: () =>
      api('/reports/bookings/detener', {
        method: 'POST',
        body: JSON.stringify({ bookingIdExterno: row.posicion, vendedorEmail: email }),
      }),
    onSuccess: () => {
      toast.success('Detención enviada (correo a los involucrados)');
      onDone();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof ApiError ? e.message : 'No se pudo detener'),
  });
  const emailOk = /.+@.+\..+/.test(email);
  return (
    <ModalShell titulo={`Detener carga · ${row.posicion}`} onClose={onClose}>
      <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-900">
        Se enviará <b>un correo de alerta</b> a administración, CxC, operación,
        calidad, el operativo y el vendedor, y <b>notificación en la campanita</b> al
        operativo, comercial, calidad y a ti. No cambia el estatus de las tareas.
      </div>
      <p className="text-sm mt-3 text-ink-700">
        <b>Instrucción:</b> Buen día, de acuerdo a instrucciones favor de detener la
        liberación de la carga con el número de posición <b>{row.posicion}</b>.
        Cualquier duda o aclaración favor de comunicarse con gerencia administrativa.
      </p>
      <label className="block text-sm mt-3 mb-1 text-ink-600">
        Correo del vendedor (comercial)
      </label>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="vendedor@strd.com.mx"
      />
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          size="sm"
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !emailOk}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {mut.isPending ? 'Enviando…' : 'Confirmar detención'}
        </Button>
      </div>
    </ModalShell>
  );
}

function ModalShell({
  titulo,
  onClose,
  children,
}: {
  titulo: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-surface-1 shadow-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold mb-3">{titulo}</h3>
        {children}
      </div>
    </div>
  );
}
